import * as React from 'react';

import { useTranslation } from '@shopee-user/platform';
import { isFeatureEnabled } from '@shopee-user/spx-customization-logic';
import { Feature } from '@shopee-user/feature-toggle';
import { useResponsiveLayout, Layout } from '@shopee-user/layout-responsive';
import { forwardOrAppendAuthParamsIfNotExist } from '@shopee-user/authentication-utils';
import { loginByOTPURL } from '@shopee-user/url';
import { isSupportThirdpartyOAuth } from '@tob/shared/thirdparty-auth/utils';

import ThirdpartyLoginMethods from '@tob/shared/thirdparty-auth/features/login/ThirdpartyLoginMethods';
import Logo from '../../../Logo';
import { Form } from '@shopee/use-form-hook';
import { LoginInputPassword } from '@shopee-user/shared-customization-ui';
import Button from '../../../Button';
import AltLoginLink from '../../../AltLoginLink';
import { Link } from 'react-router-dom';
import LoginByPasswordInputAuthKeyV2 from '../../../LoginByPasswordInputAuthKeyV2';
import LoginTermsOfService from '../../../LoginTermsOfService';
import LoginByPasswordTip from '../../../LoginByPasswordTip';
import { LoginByPasswordOneCredentialRequireAuthKeyProps } from '@shopee-user/business-customization-types';

import styles from './style.scss';
import { useLoginByPasswordOneCredentialFlowEvent } from '@tob/pages/login-by-password-one-credential-optional/flow-event';
import { ThirdpartyBindingEntryPoint } from '@tob/shared/thirdparty-auth/service/constants';

function Content({
  state: { shouldShowLoginTip, isButtonSubmitDisabled },
  components: { ResetPasswordLink },
  form,
  onSubmitForm,
  onKeyDown,
}: LoginByPasswordOneCredentialRequireAuthKeyProps) {
  const I18n = useTranslation();
  const { trackEvent } = useLoginByPasswordOneCredentialFlowEvent();

  const { layout } = useResponsiveLayout();

  const MobileLogo = layout === Layout.Mobile && (
    <div className={styles.logoContainer}>
      <Logo className={styles.logo} />
    </div>
  );

  const MobileLoginTip = shouldShowLoginTip && layout === Layout.Mobile && (
    <LoginByPasswordTip />
  );

  return (
    <>
      <div className={styles.container}>
        {MobileLogo}

        <div className={styles.formContainer}>
          {MobileLoginTip}
          <Form
            onEnterKeyDown={!isButtonSubmitDisabled ? onSubmitForm : undefined} // enable enter if captcha popup isn't shown to avoid conflict
          >
            <LoginByPasswordInputAuthKeyV2
              {...form.fields.authKey}
              wrapperClassName={styles.inputAuthKey}
              errors={form.fields.authKey.errors.map((key: string) =>
                I18n.t(key)
              )}
            />
            <LoginInputPassword
              placeholder={I18n.t('label_auth_password')}
              showError
              showErrorOutline
              {...form.fields.password}
              errors={form.fields.password.errors.map((key: string) =>
                I18n.t(key)
              )}
              extraContent={
                !isFeatureEnabled(Feature.OneCredentialSupportToBAccount) ||
                (isFeatureEnabled(Feature.ResetPasswordEntrance) &&
                  !isFeatureEnabled(
                    Feature.LoginByPasswordShowResetPasswordLinkInSmallDevice
                  )) ? (
                  <ResetPasswordLink />
                ) : null
              }
              wrapperClassName={styles.inputPassword}
            />
            <Button
              type="submit"
              className={styles.submitButton}
              text={I18n.t('label_auth_log_in')}
              disabled={isButtonSubmitDisabled}
              onClick={onSubmitForm}
            />
            <div className={styles.extraLinks}>
              {isFeatureEnabled(Feature.LoginByOtpEntrance) && (
                <div className={styles.loginWithSMSDiv}>
                  <Link
                    className={styles.link}
                    to={forwardOrAppendAuthParamsIfNotExist(
                      loginByOTPURL.getUrl()
                    )}
                    onKeyDown={onKeyDown}
                    onClick={() => trackEvent('onLoginSMSClick')}
                  >
                    {I18n.t('label_auth_login_with_sms')}
                  </Link>
                </div>
              )}
              {isSupportThirdpartyOAuth() && (
                <ThirdpartyLoginMethods
                  entryPoint={ThirdpartyBindingEntryPoint.LoginByPasswordPage}
                  className={styles.thirdpartyLoginMethods}
                  dividerClassName={styles.thirdpartyLoginMethodsDivider}
                />
              )}
              {isFeatureEnabled(Feature.LoginTermsOfService) && (
                <LoginTermsOfService />
              )}
              <AltLoginLink />
            </div>
          </Form>
        </div>
      </div>
    </>
  );
}

export default Content;
